import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { ScrollProgressBar } from '@/components/ScrollProgressBar';
import { BOX_IMAGES } from '@/data/images';

export default function ConfidentialitePage() {
  return (
    <div className="min-h-screen">
      <PageMeta
        title="Politique de confidentialité"
        description="Politique de confidentialité et RGPD de CrossFit Saint-Chamond. Gestion des données personnelles, cookies et droits des utilisateurs."
        path="/confidentialite"
      />
      <BreadcrumbJsonLd items={[{ name: 'Accueil', url: '/' }, { name: 'Confidentialité' }]} />
      <ScrollProgressBar />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/confidentialite"
        eyebrow="RGPD & vie privée"
        title="POLITIQUE DE CONFIDENTIALITÉ"
        highlight="CONFIDENTIALITÉ"
        subtitle="Comment CrossFit Saint-Chamond collecte, utilise et protège vos données personnelles."
        image={BOX_IMAGES.barbellPrep}
        alt="Politique de confidentialité CrossFit Saint-Chamond"
        breadcrumbs={[{ name: 'Accueil', href: '/' }, { name: 'Confidentialité' }]}
        minHeight="min-h-[40vh]"
      />

      <section className="section-padding bg-white" data-testid="confidentialite-content">
        <div className="container-custom max-w-3xl prose prose-neutral">
          <h2 className="font-heading text-2xl text-dark mt-8 mb-4 tracking-wide">1. Responsable du traitement</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Les données collectées sur ce site sont traitées par CrossFit Saint-Chamond — HYROX Training Club, 7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond.<br />
            Pour toute question relative à la protection de vos données : <a href="mailto:crossfit.saintchamond@gmail.com" className="text-primary">crossfit.saintchamond@gmail.com</a>.
          </p>

          <h2 className="font-heading text-2xl text-dark mt-10 mb-4 tracking-wide">2. Données collectées</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Nous collectons les données suivantes :
          </p>
          <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 space-y-1">
            <li><strong>Formulaire de contact</strong> : nom, email, téléphone (facultatif), message. Finalité : répondre à votre demande.</li>
            <li><strong>Réservation Deciplus</strong> : les données de réservation (identité, coordonnées, abonnement) sont collectées directement par notre prestataire Deciplus dans son propre espace membre. Consultez la politique de confidentialité de Deciplus sur <a href="https://deciplus.pro" target="_blank" rel="noopener noreferrer" className="text-primary">deciplus.pro</a>.</li>
            <li><strong>Analytics anonymes</strong> : pages visitées, durée de visite, source de trafic. Les données sont anonymisées (IP hashée) et utilisées exclusivement pour améliorer le site.</li>
            <li><strong>Cookies techniques</strong> : session d'authentification back-office (admin uniquement) — pas de cookies publicitaires tiers.</li>
          </ul>

          <h2 className="font-heading text-2xl text-dark mt-10 mb-4 tracking-wide">3. Durée de conservation</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Les messages issus du formulaire de contact sont conservés <strong>3 ans</strong> maximum à compter du dernier échange. Les données analytics anonymisées sont conservées <strong>13 mois</strong>. Les données de réservation sont gérées selon les conditions de Deciplus.
          </p>

          <h2 className="font-heading text-2xl text-dark mt-10 mb-4 tracking-wide">4. Destinataires</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Vos données ne sont transmises à aucun tiers à des fins commerciales. Elles peuvent être partagées avec nos sous-traitants techniques (hébergeur, Resend pour l'envoi d'emails de contact, Deciplus pour la réservation), strictement pour les besoins du service et dans le cadre d'accords garantissant la protection de vos données.
          </p>

          <h2 className="font-heading text-2xl text-dark mt-10 mb-4 tracking-wide">5. Vos droits (RGPD)</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi « Informatique et Libertés », vous disposez des droits suivants :
          </p>
          <ul className="text-muted-foreground text-sm leading-relaxed list-disc pl-5 space-y-1">
            <li>Droit d'<strong>accès</strong> à vos données</li>
            <li>Droit de <strong>rectification</strong> en cas d'erreur</li>
            <li>Droit à l'<strong>effacement</strong> (« droit à l'oubli »)</li>
            <li>Droit à la <strong>limitation</strong> du traitement</li>
            <li>Droit à la <strong>portabilité</strong> de vos données</li>
            <li>Droit d'<strong>opposition</strong> au traitement</li>
          </ul>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Pour exercer ces droits, écrivez-nous à <a href="mailto:crossfit.saintchamond@gmail.com" className="text-primary">crossfit.saintchamond@gmail.com</a>. Nous répondons dans un délai maximum d'un mois. En cas de difficulté, vous pouvez saisir la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary">cnil.fr</a>).
          </p>

          <h2 className="font-heading text-2xl text-dark mt-10 mb-4 tracking-wide">6. Cookies</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Ce site utilise uniquement des cookies strictement nécessaires au fonctionnement : cookie de session pour l'authentification du back-office (réservé à l'administrateur du site). Aucun cookie publicitaire, aucun traceur tiers. Vous pouvez à tout moment configurer votre navigateur pour refuser les cookies.
          </p>

          <h2 className="font-heading text-2xl text-dark mt-10 mb-4 tracking-wide">7. Sécurité</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Toutes les communications entre votre navigateur et nos serveurs sont chiffrées en HTTPS. Les mots de passe d'accès administrateur sont stockés hashés (bcrypt). Les données sont stockées sur des serveurs sécurisés en Europe.
          </p>

          <p className="text-muted-foreground text-xs mt-12 italic">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
